<template>
  <div>
    <!-- Step 1: Name + Collection -->
    <v2-modal
      :show="showStep1"
      title="Create New Wishlist"
      @close="onCloseStep1"
    >
      <div class="v2-mb-4">
        <v2-input
          :value="selectionName"
          label="Wishlist Name *"
          placeholder="Wishlist Name"
          @input="onInputSelectionName"
        />
      </div>
      <div class="v2-mb-4">
        <label class="v2-text-sm v2-font-bold v2-mb-1 v2-block">
          Collection <span class="v2-text-destructive">*</span>
        </label>
        <v2-dropdown
          :options="collections"
          :value="selectedCollection.name || ''"
          placeholder="Select a collection"
          label-key="name"
          value-key="_id"
          @select="onSelectCollection"
        />
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="onCloseStep1"
        >
          Close
        </v2-button>
        <v2-button
          :disabled="!isValid"
          @click="onClickNext"
        >
          Next
        </v2-button>
      </template>
    </v2-modal>

    <!-- Step 2: Import/Export -->
    <v2-modal
      :show="showStep2"
      title="Import/Export wishlist products"
      size="lg"
      @close="onHideImportModal(); showStep2 = false"
    >
      <div>
        <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4">
          <span class="v2-text-sm">
            Export {{ selectedCollection.name }} collection products
          </span>
          <v2-button
            variant="outline"
            size="sm"
            :disabled="isExporting"
            :loading="isExporting"
            @click="downloadCatalogue"
          >
            {{ isExporting ? 'Exporting' : 'Export' }}
          </v2-button>
        </div>

        <div class="v2-mb-4">
          <span class="v2-text-sm v2-mr-2">Choose import type:</span>
          <div class="v2-flex v2-gap-4 v2-mt-2">
            <label class="v2-radio">
              <input
                v-model="selectedImportOption"
                type="radio"
                value="standard"
              >
              <span class="v2-text-sm">Import - Standard</span>
            </label>
            <label class="v2-radio">
              <input
                v-model="selectedImportOption"
                type="radio"
                value="barcode"
              >
              <span class="v2-text-sm">Import - Barcode</span>
            </label>
            <label class="v2-radio">
              <input
                v-model="selectedImportOption"
                type="radio"
                value="styleCode"
              >
              <span class="v2-text-sm">Import - Style Code</span>
            </label>
          </div>
        </div>

        <div
          v-if="!productsFile"
          class="v2-upload-area"
          @click="$refs.fileInput.click()"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--v2-primary)"
            stroke-width="1.5"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line
              x1="12"
              y1="3"
              x2="12"
              y2="15"
            />
          </svg>
          <p class="v2-text-sm v2-font-bold v2-mt-2">
            Drag & Drop
          </p>
          <p class="v2-text-xs v2-text-muted">
            or click to select files from your system
          </p>
        </div>

        <div
          v-else
          class="v2-file-info"
        >
          <span class="v2-text-sm">{{ productsFile.name }}</span>
          <span class="v2-text-xs v2-text-muted v2-ml-2">
            {{ Math.round(productsFile.size / 1024) }} KB
          </span>
          <v2-button
            variant="ghost"
            size="sm"
            class="v2-ml-2"
            @click="productsFile = null"
          >
            Remove
          </v2-button>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          class="v2-hidden"
          @change="onFileSelected"
        >
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="onClickBack"
        >
          Back
        </v2-button>
        <v2-button
          :disabled="isImporting || !productsFile"
          :loading="isImporting"
          @click="onImport"
        >
          {{ isImporting ? 'Importing' : 'Import' }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { useCreateSelection } from '@/composables/useCreateSelection'

export default {
  name: 'V2CreateSelectionModal',
  components: {
 V2Modal, V2Button, V2Input, V2Dropdown,
},
  props: {
    show: { type: Boolean, default: false },
    fetchWishlists: { type: Function, default: () => {} },
  },
  setup(props) {
    const showStep1 = ref(false)
    const showStep2 = ref(false)
    const cs = useCreateSelection(props.fetchWishlists)

    return {
      showStep1,
      showStep2,
      ...cs,
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.showStep1 = true
        this.initCreateSelection()
      }
    },
  },
  methods: {
    onSelectCollection(collectionId) {
      const collection = this.collections.find(c => c._id === collectionId)
      if (collection) {
        this.onClickCollection(collection)
      }
    },
    onCloseStep1() {
      this.resetSelection()
      this.showStep1 = false
      this.$emit('close')
    },
    onClickNext() {
      analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CLICKS_ADD_SELECTION_NEXT_BUTTON, {
        selectionName: this.selectionName,
        selectedCollection: this.selectedCollection,
      })
      this.showStep1 = false
      this.showStep2 = true
    },
    onClickBack() {
      this.productsFile = null
      this.showStep2 = false
      this.showStep1 = true
      analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CLICKS_ADD_SELECTION_BACK_BUTTON)
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.productsFile = file
      }
    },
    async onImport() {
      await this.importSelection(() => {
        this.showStep2 = false
        this.$emit('close')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* removed - V2Modal body no longer clips */

.v2-text-destructive { color: var(--v2-destructive); }
.v2-text-muted { color: var(--v2-muted-foreground); }
.v2-block { display: block; }
.v2-hidden { display: none; }

.v2-radio {
  display: inline-flex;
  align-items: center;
  gap: var(--v2-space-1);
  cursor: pointer;

  input[type="radio"] {
    accent-color: var(--v2-primary);
  }
}

.v2-upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--v2-space-8);
  border: 2px dashed var(--v2-border);
  border-radius: var(--v2-radius-lg);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--v2-primary);
  }
}

.v2-file-info {
  display: flex;
  align-items: center;
  padding: var(--v2-space-3);
  background: var(--v2-muted);
  border-radius: var(--v2-radius-md);
}
</style>
